/**
 * 内容格式检测工具
 * 自动识别内容是 HTML 还是 Markdown
 */

/**
 * 检测内容是否为 HTML 格式
 */
export function isHtmlContent(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }

  const trimmed = content.trim();

  // 1. 检查是否以完整的 HTML 文档结构开始
  if (/^<!DOCTYPE\s+html>/i.test(trimmed)) {
    return true;
  }

  if (/^<html[\s>]/i.test(trimmed)) {
    return true;
  }

  // 2. 检查是否包含完整的 HTML 结构标签
  const hasHtmlStructure = /<html[\s>]/i.test(content) &&
                           /<head[\s>]/i.test(content) &&
                           /<body[\s>]/i.test(content);

  if (hasHtmlStructure) {
    return true;
  }

  // 3. 检查是否包含大量 HTML 标签（可能是 HTML 片段）
  const htmlTagPattern = /<\/?[a-z][\s\S]*?>/gi;
  const htmlTags = content.match(htmlTagPattern) || [];

  // 如果 HTML 标签数量超过 5 个，可能是 HTML
  if (htmlTags.length > 5) {
    // 检查是否有典型的 Markdown 语法
    const markdownPatterns = [
      /^#{1,6}\s+/m,        // 标题 # ## ###
      /\*\*[^*]+\*\*/,      // 粗体 **text**
      /\*[^*]+\*/,          // 斜体 *text*
      /^\s*[-*+]\s+/m,      // 无序列表
      /^\s*\d+\.\s+/m,      // 有序列表
      /\[.+?\]\(.+?\)/,     // 链接 [text](url)
      /^```/m,              // 代码块
    ];

    const hasMarkdownSyntax = markdownPatterns.some(pattern => pattern.test(content));

    // 如果有很多 HTML 标签但没有 Markdown 语法，判断为 HTML
    if (!hasMarkdownSyntax && htmlTags.length > 10) {
      return true;
    }
  }

  // 4. 检查是否有 <style> 或 <script> 标签（典型的 HTML）
  if (/<style[\s>]/i.test(content) || /<script[\s>]/i.test(content)) {
    return true;
  }

  // 默认判断为非 HTML（可能是 Markdown）
  return false;
}

/**
 * 检测内容是否为 Markdown 格式
 */
export function isMarkdownContent(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }

  // 如果是 HTML，则不是 Markdown
  if (isHtmlContent(content)) {
    return false;
  }

  const trimmed = content.trim();

  // 检查典型的 Markdown 语法
  const markdownPatterns = [
    /^#{1,6}\s+/m,        // 标题 # ## ###
    /\*\*[^*]+\*\*/,      // 粗体 **text**
    /\*[^*]+\*/,          // 斜体 *text*
    /^\s*[-*+]\s+/m,      // 无序列表
    /^\s*\d+\.\s+/m,      // 有序列表
    /\[.+?\]\(.+?\)/,     // 链接 [text](url)
    /^```/m,              // 代码块
    /^>\s+/m,             // 引用
    /^---+$/m,            // 分隔线
    /!\[.+?\]\(.+?\)/,    // 图片
  ];

  // 匹配至少 2 个 Markdown 特征
  const matchCount = markdownPatterns.filter(pattern => pattern.test(content)).length;

  return matchCount >= 2;
}

/**
 * 获取内容类型
 */
export type ContentType = 'html' | 'markdown' | 'unknown';

export function getContentType(content: string): ContentType {
  if (!content) {
    return 'unknown';
  }

  if (isHtmlContent(content)) {
    return 'html';
  }

  if (isMarkdownContent(content)) {
    return 'markdown';
  }

  return 'unknown';
}

/**
 * 测试用例（开发时使用）
 */
export const testCases = {
  html: `
<!DOCTYPE html>
<html>
<head><title>Test</title></head>
<body><h1>Hello</h1></body>
</html>
  `,

  htmlFragment: `
<div class="container">
  <h1>Title</h1>
  <p>Content</p>
  <div class="section">
    <h2>Section</h2>
  </div>
</div>
  `,

  markdown: `
# Title

This is a **bold** text and *italic* text.

## Section

- Item 1
- Item 2
- Item 3

[Link](https://example.com)
  `,

  markdownWithHtml: `
# Title

Some markdown content with <strong>HTML tags</strong>.

- List item
- Another item
  `,
};
